import type { ResourceTypeId } from '@lib/mentions';
import { Eye, MessageCircle, Pencil } from 'lucide-react';

import type { ResourceAction } from '../components/resource-actions-view';

export const MENU_ITEM_IDS = {
  UPLOAD_FILES: 'upload-files',
  CREATE_DEAL: 'create-deal',
  CREATE_TEMPLATE: 'create-template',
  INVITE_TEAM_MEMBER: 'invite-team-member',
  DOCUMENT_AUTHORING: 'document-authoring',
  CREATE_DOCUMENT_FORM: 'create-document-form',
  GENERATED_PDF: 'generated-pdf',
  DEAL_WIZARD: 'deal-wizard',
  NEW_LISTING: 'new-listing',
  CREATE_DEAL_FROM_FILE: 'create-deal-from-file',
  CREATE_DEAL_FROM_TEMPLATE: 'create-deal-from-template',
  CREATE_TEMPLATE_FROM_FILE: 'create-template-from-file',
  CREATE_EMPTY_TEMPLATE: 'create-empty-template',
  COMPANY_SETTINGS: 'company-settings',
  QUICK_SIGN: 'quick-sign',
  ADD_PARTICIPANTS: 'add-participants',
  PLAN_MODE: 'plan-mode',
  CREATE_TASK: 'create-task',
  IMPORT_TASKS: 'import-tasks',
  CREATE_KEY_DATE: 'create-key-date',
  ANALYZE_DEAL: 'analyze-deal',
  SEND_TO_SIGNERS: 'send-to-signers',
  SAVE_PROGRESS: 'save-progress',
  MANAGE_PARTICIPANTS: 'manage-participants',
  UPLOAD_PACKAGE_DOCUMENT: 'upload-package-document',
  NEW_CONTACT: 'new-contact',
  COPY_SHARE_LINK: 'copy-share-link',
  UPLOAD_PAST_DOCS: 'upload-past-docs',
  // Child-specific IDs for unified registry
  DEAL_UPLOAD_DOCUMENTS: 'deal-upload-documents',
  TEMPLATE_UPLOAD_DOCUMENTS: 'template-upload-documents',
  CREATE_EMPTY_DEAL: 'create-empty-deal',
  UPLOAD_CREATE_EMPTY_TEMPLATE: 'upload-create-empty-template',
  CREATE_FOLLOW_UP: 'create-follow-up',
  SETUP_PAYMENT: 'setup-payment',
  CREATE_KNOWLEDGE_PAGE: 'create-knowledge-page',
  REMIND: 'remind',
  SUPPORT: 'support',
} as const;

const RESOURCE_ACTION_TYPES = {
  VIEW: 'view',
  CHAT: 'chat',
  EDIT: 'edit',
} as const;

export const ANALYTICS_EVENTS = {
  DEALS_LINK_CLICKED: 'deals_link_clicked',
  TEMPLATE_LINK_CLICKED: 'template_link_clicked',
  UPLOAD_FILES_CLICKED: 'upload_files_clicked',
  TEAM_INVITE_CLICKED: 'team_invite_clicked',
  DEAL_INTENT_DETECTED: 'deal_intent_detected',
  CREATE_INTENT_SELECTED: 'create_intent_selected',
  CREATE_INTENT_TEMPLATE_SEEDED: 'create_intent_template_seeded',
  DEAL_INTENT_EXTRACTION_SETTLED: 'deal_intent_extraction_settled',
  DEAL_WIZARD_LAUNCHED: 'deal_wizard_launched',
  DEAL_WIZARD_COMPLETED: 'deal_wizard_completed',
  CREATE_EMPTY_DEAL_CLICKED: 'create_empty_deal_clicked',
  DOCUMENT_AUTHORING_CLICKED: 'document_authoring_clicked',
  DOCUMENT_AUTHORING_STARTED: 'document_authoring_started',
  DOCUMENT_CLARIFICATIONS_ASKED: 'document_clarifications_asked',
  CREATE_DOCUMENT_FORM_CLICKED: 'create_document_form_clicked',
  GENERATED_PDF_CLICKED: 'generated_pdf_clicked',
  QUICK_SIGN_CLICKED: 'quick_sign_clicked',
  PLAN_MODE_CLICKED: 'plan_mode_clicked',
  ANALYZE_DEAL_CLICKED: 'analyze_deal_clicked',
  SEND_TO_SIGNERS_CLICKED: 'send_to_signers_clicked',
  SAVE_PROGRESS_CLICKED: 'save_progress_clicked',
  SAVE_PROGRESS_SUBMITTED: 'save_progress_submitted',
  MANAGE_PARTICIPANTS_CLICKED: 'manage_participants_clicked',
  UPLOAD_PACKAGE_DOCUMENT_CLICKED: 'upload_package_document_clicked',
  CREATE_PACKAGE_LAUNCHER_OPENED: 'create_package_launcher_opened',
  CREATE_PACKAGE_OPTION_SELECTED: 'create_package_option_selected',
  PACKAGE_CREATED: 'package_created',
  UPLOAD_PAST_DOCS_CLICKED: 'upload_past_docs_clicked',
  SUPPORT_CLICKED: 'support_clicked',
  TASK_TITLE_BODY_SPLIT: 'task_form_title_body_split',
  COPY_LINK_COPIED: 'copy_link_copied',
  DELIVERY_STATUS_OPENED: 'delivery_status_opened',
  DELIVERY_STATUS_SETTLED: 'delivery_status_settled',
  DELIVERY_STATUS_DISMISSED: 'delivery_status_dismissed',
} as const;

export const QUICK_START_CHIPS = [
  'Lease Agreement',
  'NDA',
  'Commission Agreement',
  'Purchase Agreement',
  'Amendment',
  'Letter of Intent',
] as const satisfies readonly string[];

/** Derived union type — single source of truth for chip values */
export type QuickStartChip = (typeof QUICK_START_CHIPS)[number];

export const QUICK_START_CHIP_PROMPTS: Record<QuickStartChip, string> = {
  'Lease Agreement':
    'Draft a residential lease agreement including rent amount, lease term, security deposit, late fee clause, and tenant responsibilities.',
  NDA: 'Draft a mutual non-disclosure agreement covering confidential information, permitted disclosures, obligations of both parties, and a 2-year term.',
  'Commission Agreement':
    'Draft a commission agreement outlining the scope of services, commission rate, payment schedule, and termination conditions.',
  'Purchase Agreement':
    'Draft a real estate purchase agreement including purchase price, earnest money deposit, contingencies, closing date, and representations.',
  Amendment:
    'Draft an amendment to an existing agreement, specifying the original agreement details, the sections being modified, and the updated terms.',
  'Letter of Intent':
    'Draft a letter of intent for a real estate transaction outlining the proposed purchase price, key terms, due diligence period, and intended closing timeline.',
};

export const EB_ROUTES = {
  TEAM_INVITE: '/team/invite',
  TEAM: '/team',
  COMPANY_SETTINGS: '/settings',
  ASSISTANT: '/assistant',
} as const;

export const RESULT_TYPES = {
  ANNOUNCEMENT: 'announcement',
  CREATE_INTENT: 'create-intent',
  PLAN_INTENT: 'plan-intent',
  CHAT: 'chat',
  ACTION: 'action',
  NAVIGATION: 'navigation',
  CREATE_FROM_TEMPLATE: 'create-from-template',
  TEMPLATE_GROUP: 'template-group',
  RESOURCE: 'resource',
  SKILL: 'skill',
  INSIGHT: 'insight',
  INSIGHT_CHUNK: 'insight-chunk',
  CONTACT: 'contact',
  RECENT: 'recent',
  DOCUMENT: 'document',
  ADDRESS: 'address',
  DEV_TOOL: 'dev-tool',
} as const;

/**
 * Resource-specific actions configuration
 */
export const RESOURCE_ACTIONS: Record<ResourceTypeId, ResourceAction[]> = {
  deals: [
    { type: RESOURCE_ACTION_TYPES.VIEW, label: 'View deal details', icon: Eye },
    {
      type: RESOURCE_ACTION_TYPES.CHAT,
      label: 'Chat about this deal',
      icon: MessageCircle,
    },
  ],
  templates: [
    { type: RESOURCE_ACTION_TYPES.VIEW, label: 'View template', icon: Eye },
    { type: RESOURCE_ACTION_TYPES.EDIT, label: 'Edit template', icon: Pencil },
    {
      type: RESOURCE_ACTION_TYPES.CHAT,
      label: 'Chat about this template',
      icon: MessageCircle,
    },
  ],
  documents: [
    { type: RESOURCE_ACTION_TYPES.VIEW, label: 'View in deal', icon: Eye },
    {
      type: RESOURCE_ACTION_TYPES.CHAT,
      label: 'Chat about this document',
      icon: MessageCircle,
    },
  ],
  team: [
    {
      type: RESOURCE_ACTION_TYPES.CHAT,
      label: 'Chat about this team member',
      icon: MessageCircle,
    },
  ],
};
